import { Button, Empty, Input, Modal } from "antd";
import React, { useCallback, useMemo, useState } from "react";
import { useGlobalFilter, useSortBy, useTable } from "react-table";
import { ToastContainer } from "react-toastify";
import EmployeeImporter from "./EmployeeImporter"; // Asegúrate de ajustar la ruta
import ExportEmployees from "./ExportEmployees"; // Asegúrate de ajustar la ruta

// Filtro global para la búsqueda de empleados
const GlobalFilter = ({ globalFilter, setGlobalFilter }) => {
  return (
    <Input
      placeholder="Buscar empleados..."
      value={globalFilter || ""}
      onChange={(e) => setGlobalFilter(e.target.value || undefined)}
      style={{ marginBottom: "0px", width: "50%", minWidth: "250px" }}
    />
  );
};

/**
 * Tabla de empleados.
 * @param {Array} data - Lista de empleados.
 * @param {Function} refreshEmployeeList - Función para refrescar la lista de empleados.
 */
const AllEmployeeTable = ({ data, refreshEmployeeList }) => {
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [visibleRows, setVisibleRows] = useState(25);
  const [isImporterOpen, setIsImporterOpen] = useState(false); // Estado para el modal del importador

  // Maneja el éxito de la importación: cierra el modal y refresca la lista de empleados
  const handleImportSuccess = () => {
    setIsImporterOpen(false); // Cerrar el modal de importación
    if (typeof refreshEmployeeList === "function") {
      refreshEmployeeList(); // Refrescar la lista de empleados
    }
  };

  const loadMoreRows = useCallback(() => {
    setVisibleRows((prev) => prev + 25);
  }, []);

  const columns = useMemo(
    () => [
      { Header: "Apellido y Nombre", accessor: "name" },
      { Header: "CUIL", accessor: "cuil" },
      { Header: "Email", accessor: "email" },
      { 
        Header: "Estado", 
        accessor: "status",
        // Show tooltip with "motivo" property if status is "Baja"
        Cell: ({ value, row }) => {
          // Si el estado es "Baja", mostrar el motivo en un tooltip
          if (value && value.toLowerCase() === "baja" ) {
            // row.original es el objeto empleado completo
            const motivo = row.original.reason || "Sin motivo";
            return (
              <span title={`Motivo: ${motivo}`}>
                Baja
              </span>
            );
          }
          // Si no, mostrar "Activo" sin tooltip
          return "Activo";
        }
      },
      { Header: "Tipo Aporte", accessor: "tipo_aporte", Cell:({value}) => (!value || value=="")? "Sindical": value},
      {
        Header: "Ingreso Bruto",
        accessor: "salary",
        Cell: ({ value }) => `$${value}`,
      },
      { Header: "Compania", accessor: "company.name" },
    ],
    []
  );

  const tableInstance = useTable(
    { columns, data, initialState: { pageSize: 25 } },
    useGlobalFilter,
    useSortBy
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    setGlobalFilter,
    preGlobalFilteredRows,
  } = tableInstance;

  const { globalFilter } = state;

  const filteredRows = useMemo(() => rows.slice(0, visibleRows), [rows, visibleRows]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop < clientHeight + 10) {
      loadMoreRows();
    }
  };

  // Solución: el onClick debe ser una función, no el resultado de setIsImporterOpen(true)
  // Si se pasa setIsImporterOpen(true) directamente, se ejecuta en cada render y causa el loop

  return (
    
    <>
      <ToastContainer />
      {data.length === 0 ? (
        <Empty style={{ marginTop: "10%" }} description="No hay empleados cargados." />
      ) : (
        <div className="card-body" onScroll={handleScroll} style={{ height: "700px", overflowY: "auto", padding: "0px" }}>
          <div className="dataTables_wrapper">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                position: "sticky",
                top: 0,
                zIndex: 1,
                backgroundColor: "#fff",
                padding: "10px",
              }}
            >
              <GlobalFilter globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} />
              <ExportEmployees employees={data} /> {/* Botón de exportación */}
              <div>
                {/* El onClick debe ser una función anónima que llama a setIsImporterOpen */}
                <Button onClick={() => setIsImporterOpen(true)} type="primary">
                  Importar Empleados
                </Button>
              </div>
            </div>

            <table {...getTableProps()} className="table dataTable display">
              <thead
                style={{
                  position: "sticky",
                  top: "50px",
                  backgroundColor: "#fff",
                  zIndex: 1,
                }}
              >
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                        {column.render("Header")}
                        {column.canSort && (
                          <span className="ml-1">
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <i className="fa fa-arrow-down ms-2 fs-14" />
                              ) : (
                                <i className="fa fa-arrow-up ms-2 fs-14" />
                              )
                            ) : (
                              <i className="fa fa-sort ms-2 fs-14" />
                            )}
                          </span>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {filteredRows.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        
      )}
      <Modal
        title="Importar Empleados"
        open={isImporterOpen}
        onCancel={() => setIsImporterOpen(false)} // Cierra el modal
        footer={null} // Sin footer
      >
        {/* Employee import component. Pass isModalOpen to allow internal cleanup on close */}
        <EmployeeImporter
          onImportSuccess={handleImportSuccess}
          isModalOpen={isImporterOpen}
        />
      </Modal>
    </>
  );
};

export default AllEmployeeTable;
